<script>
import { preventDefault } from 'svelte/legacy';
import { toast } from "@zerodevx/svelte-toast";
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    metadataState
} from '@app/store/metadataStore.svelte.js';
import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import {
    authState
} from '@app/store/authStore.svelte.js';
import {
    fade
} from 'svelte/transition';
import Swal from 'sweetalert2';
import { SWAL_BASE, SWAL_CLASSES } from '@app/lib/swalClasses.js';
import throttle from 'just-throttle';
import { activateSubscription } from "@app/lib/activateSubscription.js";
import tcLib from '@cclib/lib/timecode.js';
import offset from "@cclib/functions/events/tcOffset.js";
import convertToPlainText from '@cclib/functions/quill/convertToPlainText.js';
import replaceTrialText from "@cclib/functions/eventGroups/replaceTrialText.js";
import {
    Document,
    Packer,
    Paragraph,
    TextRun
} from 'docx';
import eol from 'eol';
import {
    saveAs
} from 'file-saver';
import Papa from 'papaparse';
import * as m from "@app/paraglide/messages.js";

/* Firebase */
import firebase from '@app/configs/firebase.js';
import 'firebase/compat/functions';

/* Export Preset Store */
import {
    createPreset,
    getPresetsByType,
    getPresetById,
    PRESET_TYPES
} from "@app/store/presetStore.svelte.js";

let eventGroup = $state(getSelectedEventGroup(["subtitle", "translation", "audio description", "transcription"])),
    preset = $state('subtitle'), //paragraph, subtitle, table
    fileFormat = $state('txt'), //txt, csv, docx
    fileContents = "",
    fileBlob,
    exportMetadata = $state(true),
    exportSpeakers = $state(true),
    exportPrimaryLang = $state(true),
    exportIncode = $state(true),
    exportOutcode = $state(true),
    tcFormat = $state("smpte"),
    autoGroupByTc = $state(true),
    duration = $state(2),
    showOptions = $state(false),
    tcOffset = $state(tcLib.secToTc(projectState.incode, projectState.frameRate, projectState.dropFrame) || "00:00:00:00"),
    tcOffsetType = $state("add"),
    // Preset-related state
    availablePresets = $derived(getPresetsByType(PRESET_TYPES.TRANSCRIPT)),
    selectedPresetId = $state(null);

if (authState.status === "in_trial"){
    showTrialWarning();
}

function validateTc(e) {
    tcOffset = tcLib.tcValidate(e.target.value, parseFloat(projectState.frameRate), projectState.dropFrame);
}

function getSelectedEventGroup(validTypes) {
    let evGroup = eventGroupState[projectState.selected];
    if (evGroup){
        if (validTypes.indexOf(evGroup.type) > -1) {
            return evGroup;
        } else {
            return false;
        }
    } else {
        return false;
    }    
}

function showTrialWarning(){
    Swal.fire({
        ...SWAL_BASE,
        titleText: m.transcript_export_trial_title(),
        text : m.transcript_export_trial_text(),
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.transcript_export_trial_activate(),
        denyButtonText: m.transcript_export_trial_continue(),
        allowOutsideClick : false,
        allowEscapeKey : false,
        customClass: SWAL_CLASSES.trial,
    }).then((result) => {
        if (result.isConfirmed) {
            activateSubscription();
        }
    }).catch(err =>{
        console.log(err);
        console.log(err.message)
    });
}

// Function to save current settings as a preset
async function saveAsPreset() {
    const { value: presetName } = await Swal.fire({
        ...SWAL_BASE,
        titleText: m.transcript_export_preset_save_title(),
        input: 'text',
        inputLabel: m.transcript_export_preset_name_label(),
        inputPlaceholder: m.transcript_export_preset_name_placeholder(),
        showCancelButton: true,
        confirmButtonText: m.transcript_export_preset_save_confirm(),
        cancelButtonText: m.transcript_export_preset_save_cancel(),
        customClass: SWAL_CLASSES.confirmCancel,
        inputValidator: (value) => {
            if (!value) {
                return m.transcript_export_preset_name_required();
            }
        }
    });

    if (presetName) {
        try {
            const config = {
                preset,
                fileFormat,
                exportMetadata,
                exportSpeakers,
                exportPrimaryLang,
                exportIncode,
                exportOutcode,
                tcFormat,
                autoGroupByTc,
                duration,
                tcOffset,
                tcOffsetType
            };

            Object.keys(config).forEach(key => {
                if (config[key] === undefined) {
                    delete config[key];
                }
            });

            const userId = authState.user?.uid || authState.user?.id;
            const exportPreset = await createPreset({
                name: presetName,
                type: PRESET_TYPES.TRANSCRIPT,
                config
            }, userId);

            toast.push(m.transcript_export_preset_saved({ name: presetName }), { classes: ['toast-success'] });
            selectedPresetId = exportPreset.id;
        } catch (error) {
            console.error("Error saving preset:", error);
            toast.push(m.transcript_export_preset_save_failed({ error: error.message }), { classes: ['toast-danger'] });
        }
    }
}

// Function to show load preset dialog
async function showLoadPresetDialog() {
    if (availablePresets.length === 0) {
        await Swal.fire({
            ...SWAL_BASE,
            title: m.transcript_export_presets_none_title(),
            text: m.transcript_export_presets_none_text(),
            icon: 'info',
            confirmButtonText: m.ok(),
            customClass: SWAL_CLASSES.ok,
        });
        return;
    }

    // Create options object for SweetAlert select
    const presetOptions = {};
    availablePresets.forEach(presetItem => {
        presetOptions[presetItem.id] = presetItem.name;
    });

    const result = await Swal.fire({
        ...SWAL_BASE,
        title: m.transcript_export_preset_load_title(),
        input: 'select',
        inputOptions: presetOptions,
        inputPlaceholder: m.transcript_export_preset_load_placeholder(),
        showCancelButton: true,
        confirmButtonText: m.transcript_export_preset_load_confirm(),
        cancelButtonText: m.transcript_export_preset_load_cancel(),
        customClass: SWAL_CLASSES.confirmCancel,
        inputValidator: (value) => {
            if (!value) {
                return m.transcript_export_preset_load_required();
            }
        }
    });

    if (result.isConfirmed && result.value) {
        selectedPresetId = result.value;
        loadPreset(result.value);
    }
}

// Function to load a preset
function loadPreset(presetId) {
    if (!presetId) return;

    const exportPreset = getPresetById(presetId);
    if (!exportPreset || !exportPreset.config) {
        toast.push(m.transcript_export_preset_not_found(), { classes: ['toast-warning'] });
        return;
    }

    const config = exportPreset.config;
    
    // Apply config values
    if (config.preset) preset = config.preset;
    if (config.fileFormat) fileFormat = config.fileFormat;
    if (config.exportMetadata !== undefined) exportMetadata = config.exportMetadata;
    if (config.exportSpeakers !== undefined) exportSpeakers = config.exportSpeakers;
    if (config.exportPrimaryLang !== undefined) exportPrimaryLang = config.exportPrimaryLang;
    if (config.exportIncode !== undefined) exportIncode = config.exportIncode;
    if (config.exportOutcode !== undefined) exportOutcode = config.exportOutcode;
    if (config.tcFormat) tcFormat = config.tcFormat;
    if (config.autoGroupByTc !== undefined) autoGroupByTc = config.autoGroupByTc;
    if (config.duration) duration = config.duration;
    if (config.tcOffset) tcOffset = config.tcOffset;
    if (config.tcOffsetType) tcOffsetType = config.tcOffsetType;
    
    toast.push(m.transcript_export_preset_loaded({ name: exportPreset.name }), { classes: ['toast-success'] });
}

function exportTranscription() {
    let paragraphs = [],
        metadataPart = eol.after(m.transcript_export_metadata_header()),
        eventPart = "",
        csvContent = "START,END,SPEAKER,ORIGINAL,TEXT,NOTES,STYLE,TAGS\n",
        fileName = eventGroup.name;

    let copyOfEventGroup = JSON.parse(JSON.stringify(eventGroup));
    if (authState.status === "in_trial"){
        copyOfEventGroup = replaceTrialText(copyOfEventGroup);
    }

    /* tcOffset */
    if (tcOffset && tcOffset !== "00:00:00:00") {
        copyOfEventGroup.events.forEach((event, index, events) => {
            events[index] = offset(event, tcLib.tcToSec(tcOffset, projectState.frameRate, projectState.dropFrame), tcOffsetType);
        });
    }

    if (fileFormat === "csv") {
        let csvContent = Papa.unparse(JSON.stringify(
            copyOfEventGroup.events.map(event =>{
                return {
                    start : event.start,
                    end : event.end,
                    startSmpte : tcLib.secToTc(event.start, projectState.frameRate, projectState.dropFrame),
                    endSmpte : tcLib.secToTc(event.end, projectState.frameRate, projectState.dropFrame),
                    startMs : tcLib.secToTcMs(event.start),
                    endMs : tcLib.secToTcMs(event.end),
                    speakers : event.speakers.map(speaker => {return speaker.name}).join(" | "),
                    original : convertToPlainText(event.original),
                    text : convertToPlainText(event.text),
                    notes : event.notes,
                    style : event.style,
                    tags : event.tags.join(" | ")
                }
            })
        ));

        fileBlob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8"
        });

        saveAs(fileBlob, fileName + ".csv", {
            autoBom: true
        });
    } else {
        /* Metadata */
        if (exportMetadata) {
            paragraphs.push(eol.after("ID: " + projectState.id + " | Name: " + projectState.name + " | Author: " + projectState.username));

            if (metadataState.length === 0) {
                metadataPart += eol.after(m.transcript_export_metadata_none());
            } else {
                metadataState.forEach(metadata => {
                    metadataPart += eol.after(metadata.type + ": " + metadata.value);
                });
            }

            paragraphs.push(metadataPart);
        }

        if (preset === "paragraph") {
            let groupsByTc = [];
            let groupByTc = [];
            /* Group events based on the time distance between them */
            copyOfEventGroup.events.filter(event => {
                return event.text
            }).sort((eventA, eventB) => {
                return eventA.start - eventB.start;
            }).forEach((event, eventIndex, events) => {
                if (eventIndex > 0 && (!autoGroupByTc || event.start - events[eventIndex - 1].end > duration)) {
                    groupsByTc.push(groupByTc);
                    groupByTc = [event];
                } else {
                    groupByTc.push(event);
                }
            });

            if (groupByTc.length > 0) {
                groupsByTc.push(groupByTc);
                groupByTc = [];
            }

            groupsByTc.forEach(tcGroup => {
                eventPart = "";

                if (exportIncode) {
                    switch (tcFormat) {
                        case 'smpte':
                            eventPart += tcLib.secToTc(tcGroup[0].start, projectState.frameRate, projectState.dropFrame) + "\t";
                            break;
                        case 'ms':
                            eventPart += tcLib.secToTcMs(tcGroup[0].start) + "\t";
                            break;
                        default:
                            eventPart += tcGroup[0].start  + "\t";
                    }
                }

                if (exportOutcode) {
                    switch (tcFormat) {
                        case 'smpte':
                            eventPart += tcLib.secToTc(tcGroup[tcGroup.length - 1].end, projectState.frameRate, projectState.dropFrame);
                            break;
                        case 'ms':
                            eventPart += tcLib.secToTcMs(tcGroup[tcGroup.length - 1].end);
                            break;
                        default:
                            eventPart += tcGroup[tcGroup.length - 1].end;
                    }
                }

                eventPart = eol.after(eventPart);
                tcGroup.forEach(tcEvent => {
                    eventPart += convertToPlainText(tcEvent.text, " ") + " ";
                });

                paragraphs.push(eol.after(eventPart));
            });

        } else {
            /* Subtitle Preset */
            copyOfEventGroup.events.filter(event => {
                return event.text.trim();
            }).forEach((event, count) => {
                eventPart = eol.after(`${count+1})`);         

                if (exportIncode) {
                    switch (tcFormat) {
                        case 'smpte':
                            eventPart += tcLib.secToTc(event.start, projectState.frameRate, projectState.dropFrame) + "\t";
                            break;
                        case 'ms':
                            eventPart += tcLib.secToTcMs(event.start) + "\t";
                            break;
                        default:
                            eventPart += event.start + "\t";
                    }
                }

                if (exportOutcode) {
                    switch (tcFormat) {
                        case 'smpte':
                            eventPart += tcLib.secToTc(event.end, projectState.frameRate, projectState.dropFrame);
                            break;
                        case 'ms':
                            eventPart += tcLib.secToTcMs(event.end);
                            break;
                        default:
                            eventPart += event.end;
                    }
                }

                eventPart += "\n" + eol.after(convertToPlainText(event.text));

                if ((eventGroup.type === "audio description" || eventGroup.type === "transcription") && event.notes){
                    eventPart += "\n\t" + eol.after(m.transcript_export_notes_label({ notes: event.notes }));
                }
                if (eventGroup.type === "transcription" && event.tags.length > 0){                   
                    eventPart += "\n\t" + eol.after(m.transcript_export_tags_label({
                        tags: event.tags.map(tag => {return tag.term}).join(", ")
                    }));
                }

                paragraphs.push(eventPart);
            });
        }

        if (fileFormat === "txt") {
            fileContents = paragraphs.join("\n");
            fileBlob = new Blob([fileContents], {
                type: "text/plain;charset=utf-8"
            });

            saveAs(fileBlob, fileName + ".txt", {
                autoBom: true
            });
        } else {
            /* DocX */
            let textRuns = [];
            paragraphs.forEach((paragraphText) => {
                eol.split(paragraphText).forEach(pLine =>{
                    textRuns.push(
                        new TextRun({
                            text: pLine,
                            break: 1
                        })
                    );
                });
            });

            const doc = new Document({
                creator: "Closed Caption Creator Inc.",
                title: fileName,
                sections: [{
                    properties: {},
                    children: [
                        new Paragraph({
                            children: textRuns,
                        }),
                    ],
                }]
            });

            Packer.toBlob(doc).then(fileBlob => {
                saveAs(fileBlob, fileName + ".docx");
            });

        }
    }
    hideModal();
}
</script>

<div 
    transition:fade={{duration: 100}} 
    class="modal {modalState.selected === 'transcriptExport' ? 'show d-block' : ''}" 
    role="dialog" 
    aria-labelledby="transcriptExportTitle"
    aria-describedby="transcriptExportDesc"
    tabindex="-1" 
    id="TranscriptExportModal">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="transcriptExportTitle">{m.transcript_export_title()}</h4>
                <button type="button" class="btn-close" aria-label={m.transcript_export_close_label()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <p class="small text-muted" id="transcriptExportDesc"><i class="bi bi-lightning-fill" aria-hidden="true"></i> {m.transcript_export_powered_by()}</p>
                <form onsubmit={preventDefault(exportTranscription)} aria-label={m.transcript_export_form_label()}>
                    <div class="mb-3">
                        <label class="form-label" for="eventGroupSelect">{m.transcript_export_event_group_label()}</label>
                        <select class="form-select" id="eventGroupSelect" bind:value={eventGroup} aria-required="true">
                            {#each eventGroupState as eventGroupOption}
                            <option value={eventGroupOption} selected={eventGroup.id === eventGroupOption.id}>{eventGroupOption.name}</option>
                            {/each}
                        </select>
                    </div>
                    <div class="mb-3">
                        <label class="form-label" for="presetSelect">{m.transcript_export_template_preset_label()}</label>
                        <select class="form-select" id="presetSelect" bind:value={preset} aria-required="true">
                            <option value="paragraph">{m.transcript_export_template_paragraph()}</option>
                            <option value="subtitle">{m.transcript_export_template_subtitle()}</option>
                        </select>
                    </div>
                    <div class="mb-3">
                        <label class="form-label" for="formatSelect">{m.transcript_export_file_format_label()}</label>
                        <select class="form-select" id="formatSelect" bind:value={fileFormat} aria-required="true">
                            <option value="docx">.docx</option>
                            <option value="txt">.txt</option>
                            <option value="csv">.csv</option>
                        </select>
                    </div>
                    <button type="button" 
                        id="moreOptionsBtn"
                        class="btn btn-outline-warning mb-2 dropdown-toggle" 
                        onclick={()=> {showOptions = !showOptions}}
                        aria-expanded={showOptions}
                        aria-controls="advancedOptions">
                        {m.transcript_export_more_options()}
                    </button>
                    {#if showOptions}
                    <div id="advancedOptions" class="p-3 bg-secondary shadow rounded">
                        <div class="row">
                            <p class="text-warning mb-1" id="tcOptionsLabel">{m.transcript_export_timecode_options_label()}</p>
                            <div class="mb-3 col-8">
                                <label class="form-label" for="tcOffset">{m.transcript_export_tc_offset_label()}</label>
                                <input type="text" 
                                    class="form-control" 
                                    id="tcOffset"
                                    bind:value={tcOffset} 
                                    onblur={validateTc} 
                                    onfocus={(e)=>{e.target.select()}}
                                    aria-describedby="tcOffsetHelp"/>
                            </div>

                            <div class="mb-3 col-4">
                                <label class="form-label" for="tcOffsetType">{m.transcript_export_offset_type_label()}</label>
                                <select class="form-select" id="tcOffsetType" bind:value={tcOffsetType}>
                                    <option value="add">{m.transcript_export_offset_add()}</option>
                                    <option value="subtract">{m.transcript_export_offset_subtract()}</option>
                                </select>
                            </div>
                        </div>
                        {#if preset==='paragraph'}
                        <div class="row">
                            <p class="text-warning mb-1" id="groupingOptionsLabel">{m.transcript_export_grouping_options_label()}</p>
                            <div class="mb-3 col-4">
                                <div class="form-check">
                                    <input class="form-check-input" 
                                        type="checkbox" 
                                        bind:checked={autoGroupByTc} 
                                        id="groupEventsByTcCheck"
                                        aria-describedby="groupEventsHelp">
                                    <label class="form-check-label" for="groupEventsByTcCheck">
                                        {m.transcript_export_group_by_timecode_label()}
                                    </label>
                                </div>
                            </div>
                            {#if autoGroupByTc}
                            <div class="mb-3 col">
                                <label class="form-label" for="durationControlRange">
                                    {m.transcript_export_duration_label()} <span class="visually-hidden">{m.transcript_export_duration_seconds()}</span>
                                    <i class="bi bi-info-circle" aria-hidden="true"></i> 
                                    <small>{m.transcript_export_duration_value({ duration })}</small>
                                </label>
                                <input type="range" 
                                    min="0" 
                                    max="100" 
                                    step="0.5" 
                                    id="durationControlRange" 
                                    bind:value={duration}
                                    aria-describedby="durationHelp">
                                <p id="durationHelp" class="visually-hidden">{m.transcript_export_duration_help()}</p>
                            </div>
                            {/if}
                        </div>
                        <hr/>
                        {/if}
                        <div class="row">
                            <p class="text-warning mb-1">{m.transcript_export_content_options_label()}</p>
                            <div class="mb-3 col">
                                <div class="form-check">
                                    <input class="form-check-input" type="checkbox" bind:checked={exportIncode} id="exportIncodeCheck">
                                    <label class="form-check-label" for="exportIncodeCheck">
                                        {m.transcript_export_include_start_time()}
                                    </label>
                                </div>
                            </div>
                            <div class="mb-3 col">
                                <div class="form-check">
                                    <input class="form-check-input" type="checkbox" bind:checked={exportOutcode} id="exportOutcodeCheck">
                                    <label class="form-check-label" for="exportOutcodeCheck">
                                        {m.transcript_export_include_end_time()}
                                    </label>
                                </div>
                            </div>
                        </div>
                        {#if exportIncode || exportOutcode}
                        <div class="mb-3">
                            <label class="form-label" for="tcFormat">{m.transcript_export_timecode_format_label()}</label>
                            <select class="form-select" bind:value={tcFormat}>
                                <option value="smpte">{m.transcript_export_timecode_format_smpte()}</option>
                                <option value="ms">{m.transcript_export_timecode_format_ms()}</option>
                                <option value="seconds">{m.transcript_export_timecode_format_seconds()}</option>
                            </select>
                        </div>
                        {/if}
                        <hr/>
                        <div class="row">
                            {#if eventGroup && eventGroup.type === "transcription"}
                            <div class="mb-3 col">
                                <div class="form-check">
                                    <input class="form-check-input" type="checkbox" value="" bind:checked={exportSpeakers} id="exportSpeakersOption">
                                    <label class="form-check-label" for="exportSpeakersOption">
                                        {m.transcript_export_include_speakers()}
                                    </label>
                                </div>
                            </div>
                            {/if}
                            {#if fileFormat !== 'csv'}
                            <div class="mb-3 col">
                                <div class="form-check">
                                    <input class="form-check-input" type="checkbox" bind:checked={exportMetadata} id="exportMetdataOption">
                                    <label class="form-check-label" for="exportMetdataOption">
                                        {m.transcript_export_include_metadata()}
                                    </label>
                                </div>
                            </div>
                            {/if}
                            {#if eventGroup && eventGroup.type === "translation" && fileFormat === 'csv'}
                            <div class="mb-3 col">
                                <div class="form-check">
                                    <input class="form-check-input" type="checkbox" bind:checked={exportPrimaryLang} id="exportPrimaryLangOpt">
                                    <label class="form-check-label" for="exportPrimaryLangOpt">
                                        {m.transcript_export_include_original_language()}
                                    </label>
                                </div>
                            </div>
                            {/if}
                        </div>
                    </div>
                    {/if}

                </form>
            </div>
            <div class="modal-footer">
                <div class="me-auto d-flex gap-2">
                    <button
                        class="btn btn-outline-secondary"
                        type="button"
                        onclick={showLoadPresetDialog}
                        title={m.transcript_export_load_preset_title()}>
                        <i class="bi bi-folder-open" aria-hidden="true"></i> {m.transcript_export_load_preset_button()}
                    </button>
                    <button
                        class="btn btn-outline-secondary"
                        type="button"
                        onclick={saveAsPreset}
                        disabled={!eventGroup}
                        title={m.transcript_export_save_preset_title()}>
                        <i class="bi bi-save" aria-hidden="true"></i> {m.transcript_export_save_preset_button()}
                    </button>
                </div>
                <button 
                    class="btn btn-primary" 
                    type="button" 
                    onclick={exportTranscription} 
                    disabled={!eventGroup}
                    id="exportBtn"
                    aria-disabled={!eventGroup}>
                    {m.transcript_export_export_button()}
                </button>
            </div>
        </div>
    </div>
</div>

<style lang="css">
#advancedOptions {
    overflow-y: auto;
    overflow-x: none;
    max-height: 50vh;
}
</style>
